import { featureFlagManager } from '../featureFlagManager';
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

const isTestEnv =
  typeof process !== 'undefined' &&
  (process.env['NODE_ENV'] === 'test' || process.env['VITEST'] !== undefined);

interface ApiCallMetrics {
  url: string;
  method: string;
  module: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status?: number;
  error?: string;
  isRealApi: boolean;
  userId?: string;
  establishmentId?: string;
}

interface PerformanceThresholds {
  responseTime: number; // milliseconds
  errorRate: number; // percentage
  availability: number; // percentage
}

export interface ModuleHealth {
  status: 'healthy' | 'degraded' | 'critical';
  errorRate: number;
  avgResponseTime: number;
  availability: number;
  lastCheck: number;
  issues: string[];
}

export interface RealtimeModuleMetrics {
  total: number;
  api: {
    count: number;
    avgResponseTime: number;
    errorRate: number;
    p95ResponseTime: number;
    p99ResponseTime: number;
  };
  mock: {
    count: number;
    avgResponseTime: number;
    errorRate: number;
  };
  comparison: {
    speedDifference: number;
    reliabilityDifference: number;
  };
}

export interface RealtimeMetrics {
  summary: {
    totalRequests: number;
    activeRequests: number;
    overallErrorRate: number;
    overallAvgResponseTime: number;
  };
  modules: Record<string, RealtimeModuleMetrics>;
  timestamp: string;
}

class ApiMonitoringService {
  private static instance: ApiMonitoringService;
  private activeRequests: Map<string, ApiCallMetrics> = new Map();
  private completedRequests: ApiCallMetrics[] = [];
  private healthChecks: Record<string, ModuleHealth> = {};
  
  private thresholds: PerformanceThresholds = {
    responseTime: 2000, // 2 seconds
    errorRate: 5, // 5%
    availability: 95, // 95%
  };

  private monitoringEnabled = true;
  private maxStoredRequests = 1000;

  private constructor() {
    if (!isTestEnv) {
      this.startHealthMonitoring();
    }
  }

  static getInstance(): ApiMonitoringService {
    if (!ApiMonitoringService.instance) {
      ApiMonitoringService.instance = new ApiMonitoringService();
    }
    return ApiMonitoringService.instance;
  }

  // Start tracking an API call
  startRequest(config: {
    url: string;
    method: string;
    module: string;
    isRealApi: boolean;
    userId?: string;
    establishmentId?: string;
  }): string {
    const requestId = this.generateRequestId();
    const metrics: ApiCallMetrics = {
      ...config,
      startTime: performance.now(),
    };

    this.activeRequests.set(requestId, metrics);
    return requestId;
  }

  // Complete tracking an API call
  endRequest(requestId: string, status?: number, error?: Error) {
    const metrics = this.activeRequests.get(requestId);
    if (!metrics) return;

    metrics.endTime = performance.now();
    metrics.duration = metrics.endTime - metrics.startTime;
    metrics.status = status;
    if (error) {
      metrics.error = error.message;
    }

    this.activeRequests.delete(requestId);
    this.storeCompletedRequest(metrics);

    // Record in feature flag manager
    featureFlagManager.recordApiCall(
      metrics.module,
      metrics.isRealApi,
      metrics.duration,
      error
    );

    // Check if we need to trigger alerts
    this.checkForAlerts(metrics);
  }

  // Get real-time metrics
  getRealTimeMetrics(): RealtimeMetrics {
    const now = performance.now();
    const recentWindow = 5 * 60 * 1000; // Last 5 minutes
    const recentRequests = this.completedRequests.filter(
      req => now - req.startTime < recentWindow
    );

    const modules = this.getUniqueModules();
    const moduleMetrics: Record<string, RealtimeModuleMetrics> = {};

    modules.forEach(module => {
      const moduleRequests = recentRequests.filter(req => req.module === module);
      const apiRequests = moduleRequests.filter(req => req.isRealApi);
      const mockRequests = moduleRequests.filter(req => !req.isRealApi);

      moduleMetrics[module] = {
        total: moduleRequests.length,
        api: {
          count: apiRequests.length,
          avgResponseTime: this.calculateAvgResponseTime(apiRequests),
          errorRate: this.calculateErrorRate(apiRequests),
          p95ResponseTime: this.calculatePercentile(apiRequests, 95),
          p99ResponseTime: this.calculatePercentile(apiRequests, 99),
        },
        mock: {
          count: mockRequests.length,
          avgResponseTime: this.calculateAvgResponseTime(mockRequests),
          errorRate: this.calculateErrorRate(mockRequests),
        },
        comparison: {
          speedDifference: this.calculateSpeedDifference(apiRequests, mockRequests),
          reliabilityDifference: this.calculateReliabilityDifference(apiRequests, mockRequests),
        },
      };
    });

    return {
      summary: {
        totalRequests: recentRequests.length,
        activeRequests: this.activeRequests.size,
        overallErrorRate: this.calculateErrorRate(recentRequests),
        overallAvgResponseTime: this.calculateAvgResponseTime(recentRequests),
      },
      modules: moduleMetrics,
      timestamp: new Date().toISOString(),
    };
  }

  // Get module health status
  getModuleHealth(module: string): ModuleHealth;
  getModuleHealth(module?: undefined): Record<string, ModuleHealth>;
  getModuleHealth(module?: string): Record<string, ModuleHealth> | ModuleHealth {
    if (module) {
      return this.healthChecks[module] || this.calculateModuleHealth(module);
    }
    
    const allHealth: Record<string, ModuleHealth> = {};
    this.getUniqueModules().forEach(mod => {
      allHealth[mod] = this.healthChecks[mod] || this.calculateModuleHealth(mod);
    });
    
    return allHealth;
  }

  // Check if automatic rollback is needed
  shouldRollback(module: string): boolean {
    const health = this.calculateModuleHealth(module);
    
    // Critical conditions for rollback
    if (health.status === 'critical') {
      if (health.errorRate > this.thresholds.errorRate * 2) { // Double the threshold
        return true;
      }
      if (health.avgResponseTime > this.thresholds.responseTime * 3) { // Triple the threshold
        return true;
      }
      if (health.availability < this.thresholds.availability - 10) { // 10% below threshold
        return true;
      }
    }
    
    return false;
  }

  // Configure monitoring thresholds
  setThresholds(thresholds: Partial<PerformanceThresholds>) {
    this.thresholds = { ...this.thresholds, ...thresholds };
  }

  // Export monitoring data
  exportData() {
    return {
      requests: this.completedRequests,
      health: this.healthChecks,
      thresholds: this.thresholds,
      timestamp: new Date().toISOString(),
    };
  }

  // Clear monitoring data
  clearData() {
    this.completedRequests = [];
    this.activeRequests.clear();
    this.healthChecks = {};
  }

  // Private methods
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private storeCompletedRequest(metrics: ApiCallMetrics) {
    this.completedRequests.push(metrics);
    
    // Keep only recent requests
    if (this.completedRequests.length > this.maxStoredRequests) {
      this.completedRequests = this.completedRequests.slice(-this.maxStoredRequests);
    }
  }

  private getUniqueModules(): string[] {
    const modules = new Set<string>();
    this.completedRequests.forEach(req => modules.add(req.module));
    return Array.from(modules);
  }

  private calculateAvgResponseTime(requests: ApiCallMetrics[]): number {
    if (requests.length === 0) return 0;
    
    const sum = requests.reduce((acc, req) => acc + (req.duration || 0), 0);
    return sum / requests.length;
  }

  private calculateErrorRate(requests: ApiCallMetrics[]): number {
    if (requests.length === 0) return 0;
    
    const errors = requests.filter(req => req.error || (req.status && req.status >= 400));
    return (errors.length / requests.length) * 100;
  }

  private calculatePercentile(requests: ApiCallMetrics[], percentile: number): number {
    if (requests.length === 0) return 0;
    
    const sorted = requests
      .map(req => req.duration || 0)
      .sort((a, b) => a - b);
    
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[index] || 0;
  }

  private calculateSpeedDifference(apiRequests: ApiCallMetrics[], mockRequests: ApiCallMetrics[]): number {
    const apiAvg = this.calculateAvgResponseTime(apiRequests);
    const mockAvg = this.calculateAvgResponseTime(mockRequests);
    
    if (mockAvg === 0) return 0;
    return ((apiAvg - mockAvg) / mockAvg) * 100;
  }

  private calculateReliabilityDifference(apiRequests: ApiCallMetrics[], mockRequests: ApiCallMetrics[]): number {
    const apiErrorRate = this.calculateErrorRate(apiRequests);
    const mockErrorRate = this.calculateErrorRate(mockRequests);
    
    return apiErrorRate - mockErrorRate;
  }

  private calculateModuleHealth(module: string): ModuleHealth {
    const now = performance.now();
    const recentWindow = 5 * 60 * 1000; // Last 5 minutes
    const moduleRequests = this.completedRequests.filter(
      req => req.module === module && now - req.startTime < recentWindow
    );

    const apiRequests = moduleRequests.filter(req => req.isRealApi);
    const errorRate = this.calculateErrorRate(apiRequests);
    const avgResponseTime = this.calculateAvgResponseTime(apiRequests);
    const availability = apiRequests.length > 0 ? ((apiRequests.length - apiRequests.filter(req => req.error).length) / apiRequests.length) * 100 : 100;

    const issues: string[] = [];
    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';

    // Check thresholds
    if (errorRate > this.thresholds.errorRate) {
      issues.push(`High error rate: ${errorRate.toFixed(2)}%`);
      status = errorRate > this.thresholds.errorRate * 2 ? 'critical' : 'degraded';
    }

    if (avgResponseTime > this.thresholds.responseTime) {
      issues.push(`Slow response time: ${avgResponseTime.toFixed(0)}ms`);
      if (status === 'healthy') status = 'degraded';
      if (avgResponseTime > this.thresholds.responseTime * 2) status = 'critical';
    }

    if (availability < this.thresholds.availability) {
      issues.push(`Low availability: ${availability.toFixed(2)}%`);
      if (status === 'healthy') status = 'degraded';
      if (availability < this.thresholds.availability - 10) status = 'critical';
    }

    const health: ModuleHealth = {
      status,
      errorRate,
      avgResponseTime,
      availability,
      lastCheck: Date.now(),
      issues,
    };

    this.healthChecks[module] = health;
    return health;
  }

  private checkForAlerts(metrics: ApiCallMetrics) {
    // Check for critical errors
    if (metrics.error) {
      console.error(`API Error in ${metrics.module}:`, {
        url: metrics.url,
        error: metrics.error,
        duration: metrics.duration,
        isRealApi: metrics.isRealApi,
      });
    }

    // Check for slow responses
    if (metrics.duration && metrics.duration > this.thresholds.responseTime) {
      console.warn(`Slow API response in ${metrics.module}:`, {
        url: metrics.url,
        duration: metrics.duration,
        threshold: this.thresholds.responseTime,
      });
    }
  }

  private startHealthMonitoring() {
    // Run health checks every minute
    setInterval(() => {
      if (!this.monitoringEnabled) return;
      
      this.getUniqueModules().forEach(module => {
        const health = this.calculateModuleHealth(module);
        
        // Check for automatic rollback
        if (this.shouldRollback(module)) {
          console.error(`Automatic rollback triggered for module: ${module}`, health);
          featureFlagManager.rollbackModule(module);
        }
      });
    }, 60000); // Every minute
  }
}

export const apiMonitoring = ApiMonitoringService.getInstance();

interface MonitoringRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestId: string;
  };
}

function extractHeaderValue(config: MonitoringRequestConfig, header: string): string | undefined {
  const value = config.headers[header];
  const normalized = Array.isArray(value) ? value[0] : value;

  if (typeof normalized === 'string') {
    return normalized;
  }

  if (typeof normalized === 'number' || typeof normalized === 'boolean') {
    return String(normalized);
  }

  return undefined;
}

// Axios interceptor helper
export function setupApiMonitoring(axiosInstance: AxiosInstance) {
  // Request interceptor
  axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const monitoredConfig = config as MonitoringRequestConfig;
    const requestUrl = monitoredConfig.url ?? '';
    const module = extractModuleFromUrl(requestUrl);
    const isRealApi = !requestUrl.includes('/mock/');

    monitoredConfig.metadata = {
      ...monitoredConfig.metadata,
      requestId: apiMonitoring.startRequest({
        url: requestUrl || 'unknown',
        method: (monitoredConfig.method || 'GET').toUpperCase(),
        module,
        isRealApi,
        userId: extractHeaderValue(monitoredConfig, 'x-user-id'),
        establishmentId: extractHeaderValue(monitoredConfig, 'x-establishment-id'),
      }),
    };

    return monitoredConfig;
  });

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const responseConfig = response.config as MonitoringRequestConfig;
      const requestId = responseConfig.metadata?.requestId;

      if (requestId) {
        apiMonitoring.endRequest(
          requestId,
          response.status
        );
      }
      return response;
    },
    (error: AxiosError) => {
      const errorConfig = error.config as MonitoringRequestConfig | undefined;
      const requestId = errorConfig?.metadata?.requestId;

      if (requestId) {
        apiMonitoring.endRequest(
          requestId,
          error.response?.status,
          error
        );
      }
      return Promise.reject(error);
    }
  );
}

function extractModuleFromUrl(url: string): string {
  const patterns = [
    { pattern: /\/users/i, module: 'users' },
    { pattern: /\/establishments|\/condominiums/i, module: 'condominiums' },
    { pattern: /\/support-tickets/i, module: 'supportTickets' },
    { pattern: /\/maintenances?/i, module: 'maintenance' },
    { pattern: /\/documents/i, module: 'documents' },
    { pattern: /\/communications/i, module: 'communications' },
    { pattern: /\/governance/i, module: 'governance' },
    { pattern: /\/delinquencies|\/payment-agreements|\/finance/i, module: 'finance' },
  ];

  for (const { pattern, module } of patterns) {
    if (pattern.test(url)) {
      return module;
    }
  }

  return 'unknown';
}
